/** The recap's JSON, exactly as `src/discord_stats/wrapped` builds it. */

export type Act = "you" | "chat" | "friends" | "share";

/** Every award the build can hand out (`wrapped/awards.py`), fallbacks last. */
export const AWARD_IDS = [
  "icebreaker", "speed_demon", "left_on_read", "novelist", "one_word_wonder", "editor", "double_texter",
  "three_am_club", "early_bird", "last_word", "necromancer", "caps_lock", "question_mark", "streak",
  "call_captain", "call_starter", "hall_of_fame", "comedian", "laugh_track", "main_character", "summoner",
  "reply_magnet", "viral_moment", "ghost", "comeback_kid", "apologiser", "exclamation_station",
  "nooooo_champion", "lowercase_loyalist", "full_stop", "emoji_overlord", "meme_lord", "gif_goblin",
  "link_dumper", "the_dj", "rename_royalty", "weekend_warrior", "spoiler_alert",
  "special_guest_star", "quality_over_quantity",
] as const;

/** The sixteen chat personalities (`wrapped/personality.py`). */
export const TYPE_NAMES = [
  "The Town Crier", "The Meme Courier", "The Columnist", "The Curator",
  "The Hype Squad", "The Reaction GIF", "The Agony Aunt", "The Fact Checker",
  "The 2 AM Thought", "The Midnight Meme Dealer", "The Midnight Novelist", "The Rabbit Hole",
  "The Night Watch", "The Sleepless Reactor", "The Late-Night Therapist", "The Night Librarian",
] as const;

export type Period = { title: string; start: string; end: string; timezone: string };
export type Award = { id: string; name: string; stat: string; value: number; fallback: boolean };
export type Line = { at: string; text: string };
export type Traits = {
  time: "night" | "day";
  role: "starter" | "replier";
  length: "short" | "long";
  medium: "words" | "memes";
};
/** A GIF as a card plays it: kept on this server under `id`, with its source as a fallback. */
export type GifCard = { id: string; count: number; width: number; height: number; source: string };
export type Poll = { at: string; by: string; question: string; answers: { text: string; votes: number }[] };
export type Personality = { type: string; traits: Traits; reasons: Record<keyof Traits, string> };
export type You = {
  messages: { total: number; per_day: number; share: number };
  top_people: { name: string; replies: number }[];
  words: { signature: string | null; top: { word: string; count: number }[] };
  emoji: { emoji: string; count: number }[];
  sites: { domain: string; count: number }[];
  clock: { peak_hour: number; by_hour: number[]; kind: "night_owl" | "early_bird" | "daytime" | "evening" };
  biggest_day: { date: string; count: number; messages: Line[] } | null;
  streak: { days: number; start: string; end: string; active_days: string[] } | null;
  then_vs_now: { previous_total: number | null; change: number | null; first_message: Line | null };
  calls: { hours: number; count: number; longest_minutes: number; longest_date: string } | null;
  /** Missing from builds made before GIF cards existed. */
  gif?: GifCard | null;
  awards: Award[];
  personality: Personality;
};
export type PersonStory = { person: { id: string; name: string }; you: You };
export type Chat = {
  numbers: {
    messages: number;
    people: number;
    busiest_day: { date: string; count: number } | null;
    quietest_week: { start: string; count: number } | null;
  };
  names: { at: string; name: string; by: string | null }[];
  calls: { hours: number; count: number; longest: { minutes: number; date: string; people: string[] } } | null;
  word_of_month: { month: string; word: string | null }[];
  joins: { at: string; name: string; kind: "joined" | "left" }[];
  /** These four are missing from builds made before their cards existed. */
  gif?: (GifCard & { by: string }) | null;
  duo?: { a: string; b: string; a_to_b: number; b_to_a: number } | null;
  /** Messages by weekday (Monday first) and hour, in the chat's time. */
  heat?: number[][];
  polls?: Poll[];
  quiz: { question: string; options: string[]; answer: string; stat: string }[];
  ceremony: { name: string; awards: Award[]; personality: string }[];
};
export type WrappedResponse = {
  period: Period;
  status: "draft" | "released";
  me: PersonStory | null;
  chat: Chat;
  /** Profile pictures on this server, by the name the cards show. */
  avatars?: Record<string, string>;
  /** The story's background song, if an admin uploaded one. */
  music?: string | null;
};
export type Song = { name: string; size: number; content_type: string; uploaded_at: string };
/** `draft` is only ever true for an admin: a build exists that isn't out yet. */
export type Available = { released: boolean; title: string | null; draft?: boolean };
export type WrappedAdmin = {
  settings: { end: string; title: string };
  build: {
    state: "idle" | "running" | "done" | "failed";
    started_at: string | null;
    finished_at: string | null;
    error: string | null;
  };
  period: (Period & { id: number; released: boolean; built_at: string; source_count: number }) | null;
  people: { id: string; name: string; messages: number }[];
  music?: Song | null;
};
